/**
 * The per-render trace scope, published on `globalThis` rather than exported directly.
 *
 * The indirection is deliberate. `__root.tsx`'s `head()` needs to read the current render's trace ids
 * so it can bake them into the HTML, but `__root.tsx` is part of the CLIENT bundle too — importing a
 * module that pulls in `node:async_hooks` would break it. Reading an optional global instead keeps the
 * client side of the file free of any server import, and simply yields `undefined` in the browser.
 *
 * This file is isomorphic and must stay free of server-only imports. The store itself is created and
 * published by `src/server.ts` (server-only).
 */
export interface SsrTrace {
	traceId: string;
	rootSpanId: string;
	/**
	 * The path + search this document was rendered for, so the browser can tell whether it is hydrating
	 * the document that was rendered for its own URL (TEC-3854).
	 *
	 * Meta's crawler reuses one fetched document across many facet URLs. Hydrating document A under URL
	 * B makes the dehydrated match id disagree with the computed one — the id encodes `loaderDeps` —
	 * so the router refuses the SSR markup, client-renders through `ClientOnly`, and paints the route's
	 * pending component over the real page. React reports `#418 args[]=HTML`. Nothing is broken; the
	 * document and the URL simply disagree, and the report is not actionable.
	 *
	 * Optional because a render outside a request scope has no URL to record.
	 */
	url?: string;
}

interface SsrTraceStore {
	getStore(): SsrTrace | undefined;
}

/**
 * The trace ids for the render currently in progress, or undefined outside an SSR render (which
 * includes every call made from the browser).
 */
export function currentSsrTrace(): SsrTrace | undefined {
	return (globalThis as { __ssrTraceAls?: SsrTraceStore }).__ssrTraceAls?.getStore();
}

/**
 * The URL the document being rendered was rendered FOR: the in-flight request's path on the server,
 * and in the browser the path the server baked into the HTML it sent.
 *
 * Not the same thing as `location` — which is the whole point. See {@link documentReplayed}.
 */
export function currentDocumentUrl(): string | undefined {
	const fromRender = currentSsrTrace()?.url;
	if (fromRender) return fromRender;
	if (typeof window === 'undefined') return undefined;
	return window.__RENDER_URL || undefined;
}

/**
 * Is this browser hydrating a document that was rendered for a DIFFERENT URL?
 *
 * It sounds impossible, and for a human it is. For a crawler it is routine: TEC-3854 was one client
 * fetching a portal home once and then hydrating that same HTML under many facet URLs — 49 of 95
 * distinct trace ids in a 30-day window were seen hydrating under more than one URL. The mismatch that
 * follows is guaranteed rather than incidental, because a dehydrated match id encodes the route's
 * `loaderDeps` (the facets), so ids computed from URL B cannot match a payload dehydrated from URL A.
 * TanStack then abandons the match and renders the pending component OVER the real page, which is the
 * `#418 args[]=HTML` those reports carry.
 *
 * HOW A CRAWLER MANAGES THIS AT ALL, since a browser cannot. In a crawl pipeline the response body
 * and the URL travel SEPARATELY: one stage fetches URLs and stores bodies, a later stage loads a body
 * into a headless browser to run its JS, and that stage has to be TOLD which URL the body belongs to.
 * If the store is keyed on a NORMALIZED url — query string dropped or reordered, or a canonical
 * collapsing `?year=2019` into `/` as a duplicate — then one stored body answers for every facet
 * variant and gets rendered under whichever one was discovered.
 *
 * Which is why this lands on the facets specifically, and on nothing else: they are the only URLs
 * whose path is identical and whose QUERY alone differs, while the query is exactly what feeds
 * `loaderDeps`. Query-dropping normalisation is harmless on almost any other site and precisely fatal
 * here. Note the self-reinforcing part, because it is what the fix for TEC-3864 addresses: rendering
 * the filters as links generated a combinatorial facet space (year x causes x state), which is itself
 * the reason a crawler would normalise and dedupe those URLs in the first place.
 *
 * REPRODUCED, rather than reasoned about. Serving document A from URL B's own origin (Playwright
 * `route.fulfill`, so the origin is identical and the only difference is which URL the document was
 * rendered for) raises the mismatch every time, and React's dev component stack names the chain
 * exactly: `MatchView` -> `ClientOnly` -> `HomePending`, with the skeleton `div` replacing the
 * server's real subtree. The control — document B at URL B — is clean.
 *
 * There is no code change that can make that hydrate cleanly — the document genuinely does not belong
 * to the URL — so this exists to ATTRIBUTE it rather than to prevent it: the event is still recorded,
 * and only the Jira ticket is skipped. Which is also why it must not be inferred from a user-agent:
 * `isbot()` misses the reused-document case that matters most here, and the URL comparison is direct
 * evidence rather than a guess about who is asking.
 *
 * THREE states, and the third is the point (TEC-3881). `true` replayed, `false` checked and clean, and
 * `undefined` when the question could not be asked at all — no baked-in URL to compare against, or no
 * `location`. It returned `false` for that last case until the build comparison existed, and that was
 * right while this verdict alone decided the ticket: `false` meant "ticket it", which fails closed.
 *
 * It stopped being right once `reportIsUnattributable` (`build-assets.ts`) started reading an ABSENT
 * verdict as "this browser never made the call" and falling back to comparing the build. A `false` now
 * carries more weight than it used to — it asserts the browser looked and the document belongs here,
 * which OUTRANKS the build comparison by design. Returning it for "I could not look" claims knowledge
 * this function does not have, and silently blocks the fallback on precisely the documents that need
 * it: one served without its head bootstrap, because a proxy stripped it, a translator rewrote `<head>`,
 * or a crawler re-serialized the body. Those run current code and still cannot answer, so as the
 * pre-fix bundles age out they would have become the population the attribution can never reach.
 *
 * Still fails CLOSED, now as a composite rather than alone: the ticket branch in `data.ts` tests this
 * truthily, so `undefined` tickets exactly as `false` did, and suppression additionally requires
 * positive evidence of an old bundle. A missed attribution is a noisy ticket; a wrong one silently
 * drops a real bug.
 */
export function documentReplayed(): boolean | undefined {
	if (typeof window === 'undefined') return undefined;
	const rendered = currentDocumentUrl();
	if (!rendered) return undefined;
	return rendered !== window.location.pathname + window.location.search;
}

/**
 * The trace ids of the document being rendered: the in-progress render's ids on the server, and on the
 * client the ids that same render baked into the HTML.
 *
 * Reading them back in the browser is what keeps `head()` isomorphic: an entry the server renders and
 * the client doesn't shifts every later head entry by one position, so React hydrates one script
 * against another and rejects the whole tree (#418, `args[]=HTML`).
 *
 * Returns undefined when there is no trace, INCLUDING when the browser reads back the empty ids that
 * `head()` emits in that case — it always emits the script, so callers must treat empty as absent and
 * re-emit the same empty values rather than dropping the entry.
 */
export function currentDocumentTrace(): SsrTrace | undefined {
	const ssrTrace = currentSsrTrace();
	if (ssrTrace) return ssrTrace;
	if (typeof window === 'undefined') return undefined;
	const { __TRACE_ID: traceId, __ROOT_SPAN_ID: rootSpanId } = window;
	return traceId && rootSpanId ? { traceId, rootSpanId, url: window.__RENDER_URL || undefined } : undefined;
}
